import type { Candle, Instrument, Timeframe } from "@trader/shared/MarketData"
import type { IChartApi, ISeriesApi, MouseEventParams, SeriesType } from "lightweight-charts"
import type { ChartContext, IndicatorRegistryEntry } from "lightweight-charts-indicators"
import type { IndicatorRenderer } from "lightweight-charts-indicators/render"
import type { IndicatorStyle } from "../workspace/indicatorStyleSchema.ts"
import type { IndicatorInstance } from "../workspace/workspaceStore.ts"
import { chartContextFor } from "./chartContext.ts"
import type { IndicatorModules } from "./indicatorModules.ts"
import { type IndicatorResult, legendValues, type LegendValue, renderOptions, type Styled, styleEntry } from "./indicatorStyle.ts"

export type { LegendValue } from "./indicatorStyle.ts"

interface Drawn {
  instance: IndicatorInstance
  readonly entry: IndicatorRegistryEntry
  readonly renderer: IndicatorRenderer
  styled: Styled
  /** The settings dialog's unsaved style, drawn while it's open. */
  preview: Styled | null
  /** The computed values for `inputsKey` on the current bars (null: compute again). */
  result: IndicatorResult | null
  inputsKey: string
}

/** Indicator panes get a quarter of the price pane's height, as in TradingView. */
const PRICE_PANE_STRETCH = 4

const inputsOf = (entry: IndicatorRegistryEntry, instance: IndicatorInstance): Record<string, unknown> => ({
  ...(entry.defaultInputs as Record<string, unknown>),
  ...instance.inputs
})

/** Same indicators in the same order and visibility: only inputs or styles changed. */
const sameLayout = (drawn: ReadonlyArray<Drawn>, shown: ReadonlyArray<IndicatorInstance>): boolean =>
  drawn.length === shown.length && drawn.every((d, i) => d.instance.uid === shown[i]!.uid && d.instance.id === shown[i]!.id)

/**
 * The indicators of one chart pane: computes each one on the pane's bars and
 * draws it with one IndicatorRenderer, in the instance's style. Overlays share
 * the price pane; every other indicator gets its own pane below, in the order
 * they were added. Results are kept until the bars or the inputs change, so a
 * style change or a hide redraws without computing. Plain TypeScript; ChartPane
 * owns its lifetime.
 */
export class IndicatorLayer {
  private readonly chart: IChartApi
  private readonly mainSeries: ISeriesApi<SeriesType>
  private readonly modules: IndicatorModules
  private drawn: Array<Drawn> = []
  private bars: ReadonlyArray<Candle> = []
  private context: ChartContext | null = null
  private chartKey = ""
  private symbolPrecision = 2
  /** Results of indicators taken off the chart (hidden), kept while the bars stay the same. */
  private readonly parked = new Map<string, { readonly inputsKey: string; readonly result: IndicatorResult }>()
  /** How many panes the chart had when they were last sized (resized only when that changes). */
  private sizedPanes = -1
  /** Why an indicator couldn't be drawn, by instance uid. */
  readonly errors = new Map<string, string>()

  constructor(chart: IChartApi, mainSeries: ISeriesApi<SeriesType>, modules: IndicatorModules) {
    this.chart = chart
    this.mainSeries = mainSeries
    this.modules = modules
  }

  /** The chart's symbol and timeframe; a change drops every computed result. */
  setChart(instrument: Instrument, timeframe: Timeframe): void {
    const key = `${instrument.id}|${timeframe}`
    if (key === this.chartKey) return
    this.chartKey = key
    this.context = chartContextFor(instrument, timeframe)
    this.symbolPrecision = instrument.pricePrecision
    this.forgetResults()
    for (const d of this.drawn) d.styled = styleEntry(d.entry, d.instance.style, this.symbolPrecision)
  }

  /**
   * The indicator set changed. When only inputs or styles did, each indicator is
   * redrawn in place (its series and pane kept, so pane heights stay); otherwise
   * the set is drawn again, reusing results that are still valid.
   */
  setIndicators(instances: ReadonlyArray<IndicatorInstance>): void {
    const shown = instances.filter((instance) => !instance.hidden && this.modules.byId.has(instance.id))
    if (sameLayout(this.drawn, shown)) {
      this.drawn.forEach((d, i) => this.updateInstance(d, shown[i]!))
      // Series are redrawn, not reused: reuse matches series by creation order, and a
      // changed plot type or a hidden plot shifts that order (a level could take over a
      // hidden line's invisible series). The renderer keeps the indicator's pane.
      this.renderAll(false)
      return
    }
    for (const d of this.drawn) if (d.result !== null) this.parked.set(d.instance.uid, { inputsKey: d.inputsKey, result: d.result })
    this.clear()
    let nextPane = 1
    for (const instance of shown) {
      const entry = this.modules.byId.get(instance.id)!
      const paneIndex = entry.overlay ? 0 : nextPane++
      const renderer = new this.modules.IndicatorRenderer(this.chart, { paneIndex, mainSeries: this.mainSeries })
      const inputsKey = JSON.stringify(instance.inputs)
      const parked = this.parked.get(instance.uid)
      this.drawn.push({
        instance,
        entry,
        renderer,
        styled: styleEntry(entry, instance.style, this.symbolPrecision),
        preview: null,
        result: parked?.inputsKey === inputsKey ? parked.result : null,
        inputsKey
      })
    }
    const kept = new Set(instances.map((instance) => instance.uid))
    for (const uid of this.parked.keys()) if (!kept.has(uid)) this.parked.delete(uid)
    this.renderAll(false)
  }

  /** New bars: all of them, or only the newest (tail refresh) so series are updated in place. */
  setBars(bars: ReadonlyArray<Candle>, tailOnly: boolean): void {
    this.bars = bars
    this.forgetResults()
    this.renderAll(tailOnly)
  }

  /** Draws an unsaved style while the settings dialog is open (null: back to the saved one). */
  preview(uid: string, style: IndicatorStyle | undefined | null): void {
    const d = this.drawn.find((drawn) => drawn.instance.uid === uid)
    if (d === undefined) return
    d.preview = style === null ? null : styleEntry(d.entry, style, this.symbolPrecision)
    this.renderOne(d, false)
  }

  /** The indicator's last computed values (for the settings dialog's fill and palette rows). */
  resultOf(uid: string): IndicatorResult | null {
    return this.drawn.find((d) => d.instance.uid === uid)?.result ?? null
  }

  /** The style shown for an indicator (its preview while the dialog is open): precision and format for the legend. */
  styledOf(uid: string): Styled | undefined {
    const d = this.drawn.find((drawn) => drawn.instance.uid === uid)
    return d === undefined ? undefined : d.preview ?? d.styled
  }

  /**
   * Each indicator's plot values at the crosshair (or the latest bar), for the
   * legend, in the colours drawn. Plots with no value there are left out.
   */
  valuesAt(param: MouseEventParams | null): Map<string, ReadonlyArray<LegendValue>> {
    const time = typeof param?.time === "number" ? param.time : null
    const values = new Map<string, ReadonlyArray<LegendValue>>()
    for (const d of this.drawn) {
      if (d.result === null) continue
      values.set(d.instance.uid, legendValues(d.preview ?? d.styled, d.result, inputsOf(d.entry, d.instance), time))
    }
    return values
  }

  /** The indicator that drew this series, if any (to select an indicator by clicking its line). */
  instanceOf(series: ISeriesApi<SeriesType> | undefined): IndicatorInstance | undefined {
    if (series === undefined) return undefined
    return this.drawn.find(({ renderer }) => renderer.series().includes(series))?.instance
  }

  dispose(): void {
    this.clear()
    this.parked.clear()
  }

  private updateInstance(d: Drawn, instance: IndicatorInstance): void {
    const inputsKey = JSON.stringify(instance.inputs)
    if (inputsKey !== d.inputsKey) {
      d.inputsKey = inputsKey
      d.result = null
    }
    if (instance.style !== d.instance.style) d.styled = styleEntry(d.entry, instance.style, this.symbolPrecision)
    d.instance = instance
    d.preview = null
  }

  private forgetResults(): void {
    for (const d of this.drawn) d.result = null
    this.parked.clear()
  }

  private clear(): void {
    for (const { renderer } of this.drawn) renderer.clear()
    this.drawn = []
    this.errors.clear()
  }

  private renderAll(reuseSeries: boolean): void {
    if (this.bars.length === 0 || this.context === null) {
      // No bars (another symbol loading, or none at all): nothing of the old ones stays drawn.
      for (const d of this.drawn) d.renderer.clear()
      return
    }
    const bars = this.bars.map((bar) => ({ ...bar }))
    for (const d of this.drawn) this.renderOne(d, reuseSeries, bars)
    this.sizePanes()
  }

  private renderOne(d: Drawn, reuseSeries: boolean, bars = this.bars.map((bar) => ({ ...bar }))): void {
    if (bars.length === 0 || this.context === null) return
    try {
      const inputs = inputsOf(d.entry, d.instance)
      d.result ??= d.entry.calculate(bars, inputs, this.context) as IndicatorResult
      const styled = d.preview ?? d.styled
      const { plots, result } = renderOptions(styled, d.result)
      d.renderer.render(styled.renderable, result, bars, {
        inputs,
        plots,
        lastValueVisible: styled.lastValueVisible,
        precision: styled.precision,
        reuseSeries
      })
      this.errors.delete(d.instance.uid)
    } catch (error) {
      this.errors.set(d.instance.uid, error instanceof Error ? error.message : String(error))
    }
  }

  /** Only when panes were added or removed: heights the user dragged survive redraws and live updates. */
  private sizePanes(): void {
    const panes = this.chart.panes()
    if (panes.length === this.sizedPanes) return
    this.sizedPanes = panes.length
    panes.forEach((pane, index) => pane.setStretchFactor(index === 0 ? PRICE_PANE_STRETCH : 1))
  }
}
